import { useCallback, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getNotificationPreferences,
  createNotificationPreferences,
  updateNotificationPreferences,
  type NotificationPreferencesInput,
} from '../services/notificationPreferences';

/**
 * React Query hook for notification preferences
 *
 * Features:
 * - Automatic fetching when user is authenticated
 * - Optimistic updates for better UX
 * - Error handling with retry logic
 * - Cache invalidation on mutations
 *
 * Usage:
 *   const { preferences, isLoading, updatePreferences } = useNotificationPreferences({
 *     userId: user?.id,
 *     enabled: isAuthenticated,
 *   });
 *   updatePreferences.mutate({ pushEnabled: true });
 */

interface UseNotificationPreferencesOptions {
  userId?: string;
  enabled: boolean;
}

type NotificationPreferences = NonNullable<
  Awaited<ReturnType<typeof getNotificationPreferences>>
>;

export function useNotificationPreferences({
  userId,
  enabled,
}: UseNotificationPreferencesOptions) {
  const queryClient = useQueryClient();
  const initializationPromiseRef = useRef<Promise<NotificationPreferences> | null>(null);

  const queryKey = ['notificationPreferences', userId];

  // Fetch preferences
  const {
    data: preferences,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey,
    queryFn: () => {
      if (!userId) {
        throw new Error('User ID is required');
      }
      return getNotificationPreferences(userId);
    },
    enabled: enabled && !!userId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 2,
  });

  // Create preferences mutation
  const createMutation = useMutation({
    mutationFn: (input: Partial<NotificationPreferencesInput>) => {
      if (!userId) {
        throw new Error('User ID is required');
      }
      return createNotificationPreferences(userId, input);
    },
    onSuccess: (data) => {
      queryClient.setQueryData(queryKey, data);
    },
    onError: () => {
      // Failed to create notification preferences (console suppressed)
    },
  });
  const createPreferencesAsync = createMutation.mutateAsync;

  // Update preferences mutation with optimistic updates
  const updateMutation = useMutation({
    mutationFn: (updates: Partial<NotificationPreferencesInput>) => {
      if (!userId) {
        throw new Error('User ID is required');
      }
      return updateNotificationPreferences(userId, updates);
    },
    onMutate: async (updates) => {
      await queryClient.cancelQueries({ queryKey });
      const previousPreferences = queryClient.getQueryData(queryKey);

      queryClient.setQueryData(queryKey, (old: unknown) => {
        if (!old) return old;
        return { ...(old as object), ...updates };
      });

      return { previousPreferences };
    },
    onError: (_error, _variables, context) => {
      if (context?.previousPreferences) {
        queryClient.setQueryData(queryKey, context.previousPreferences);
      }
      // Failed to update notification preferences (console suppressed)
    },
    onSuccess: (data) => {
      queryClient.setQueryData(queryKey, data);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  // Helper to initialize preferences if they don't exist
  const initializePreferences = useCallback(
    (defaults?: Partial<NotificationPreferencesInput>): Promise<NotificationPreferences> => {
      if (preferences) {
        return Promise.resolve(preferences);
      }

      if (!userId) {
        return Promise.reject(new Error('User ID is required'));
      }

      if (!initializationPromiseRef.current) {
        const initializationPromise = createPreferencesAsync(defaults || {});
        initializationPromiseRef.current = initializationPromise;

        initializationPromise.then(
          () => {
            if (initializationPromiseRef.current === initializationPromise) {
              initializationPromiseRef.current = null;
            }
          },
          () => {
            if (initializationPromiseRef.current === initializationPromise) {
              initializationPromiseRef.current = null;
            }
          }
        );
      }

      return initializationPromiseRef.current;
    },
    [createPreferencesAsync, preferences, userId]
  );

  return {
    // Data
    preferences,
    hasPreferences: !!preferences,

    // Loading states
    isLoading,
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,

    // Error states
    error,
    createError: createMutation.error,
    updateError: updateMutation.error,

    // Mutations
    createPreferences: createMutation.mutate,
    updatePreferences: updateMutation.mutate,
    initializePreferences,

    // Async versions for promise handling
    createPreferencesAsync: createMutation.mutateAsync,
    updatePreferencesAsync: updateMutation.mutateAsync,

    // Utilities
    refetch,
  };
}
